import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { UserPlus } from "lucide-react";
import ModalShell from "../../../../_components/modal/ModalShell";
import { TextInput, PhoneInput, Checkbox, Banner } from "../../../../_components/ui";
import { useToast } from "../../../../_context/ToastContext/ToastContext";
import { useGetDataMutation } from "../../../../_filtering/api/dataApiSlice";
import { setData } from "../../../../_filtering/state/dataSlice";
import {
  useCreateContactMutation,
  useUpdateContactMutation,
  useGetContactByEmailMutation,
} from "../../../../_features/contacts/api/contactApiSlice";
import "./_contact-only-modal.scss";

const EMPTY_FORM = {
  contactName: "",
  mail: "",
  phoneNumber: "",
  designation: "",
  hasBillingPortalAccess: false,
};

/**
 * Create/edit a company-independent Contact document - moved from
 * src/modals/ContactFormModal/ContactOnlyModal.js. Uses the fat
 * `_features/contacts/api/contactApiSlice.js` for create/update (same slice
 * every other real consumer of this endpoint uses - see docs/bug-sheet.md
 * for the fat/lean collision this migration resolved by not duplicating
 * these endpoints into `_features/contacts`' own api file).
 *
 * `formData.phoneNumber` stays a single "<dialCode> <phoneNumber>" string
 * for display, matching `_components/ui/PhoneInput`'s own value shape -
 * joined from the raw contact's separate `countryCode`/`phoneNumber` fields
 * in the edit-mode effect below (mirrors what CustomerContactModal used to
 * do internally), so every caller can pass the raw contact record straight
 * through as `rowData`. But the backend
 * (`contact.controller.js` createContact/updateContact) destructures
 * separate `phoneNumber`/`countryCode` fields from the request body -
 * confirmed against `CustomerContactModal`, the other real caller of the
 * same endpoints, which splits on submit (`formData.phoneNumber.split("
 * ")`) rather than sending the combined string. Without the same split
 * here, every contact created/edited through this modal saved the full
 * "+1 2025551234" string into `phoneNumber` and left `countryCode` unset.
 *
 * Real bug fix: the old modal's catch block only logged the error and hid
 * the loading toast - it never showed the user a failure toast at all,
 * violating this codebase's ".unwrap() + user-facing error" rule. Fixed here.
 *
 * Second real bug fix: `Contacts.js`'s schema has `designation` as
 * `required: true`, but this form let you submit without one - createContact
 * has no runValidators guard against it, so the raw Mongoose error
 * ("Contacts validation failed: designation: Path `designation` is
 * required.") surfaced straight to the user as the failure toast. Same bug
 * CustomerContactModal already documents and validates against for the
 * sibling contact-creation flow; mirrored here.
 */
const ContactOnlyModal = ({
  isOpen,
  onClose,
  mode = "create",
  rowData = null,
  onSuccess,
  refetchFilters,
  companyId = null,
}) => {
  const { showToast, hideToast } = useToast();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Ported from the retired CustomerContactModal - createContact 409s when
  // the mail/phone already belongs to another contact in this org, and
  // that's very often not a mistake but the exact contact the user meant
  // (e.g. adding someone who's already a contact on a different company).
  // Rather than a dead-end error, this offers to look the existing contact
  // up and hand it back as if it had just been created.
  const [duplicateContactError, setDuplicateContactError] = useState(null);
  const [isAddingExistingContact, setIsAddingExistingContact] = useState(false);

  const [getDataApiCall] = useGetDataMutation();
  const [createContactApiCall] = useCreateContactMutation();
  const [updateContactApiCall] = useUpdateContactMutation();
  const [getContactByEmailApiCall] = useGetContactByEmailMutation();

  useEffect(() => {
    if (rowData && mode === "edit") {
      // rowData carries the raw Contact document - separate `countryCode`/
      // `phoneNumber` fields - but PhoneInput's `value` is a single
      // "<dialCode> <phoneNumber>" string (see PhoneInput's own docstring).
      // Joined here (not by callers) so every caller can just pass the raw
      // contact through, same as CustomerContactModal used to do internally.
      setFormData({
        contactName: rowData.contactName || "",
        mail: rowData.mail || "",
        // Always joined, even when countryCode is "" - PhoneInput's
        // parseValue splits on the first space, so a bare digit string with
        // no space (no countryCode) gets misread as the dial code itself
        // and renders blank. The leading space from an empty countryCode
        // parses correctly (empty dial code + full number).
        phoneNumber: `${rowData.countryCode || ""} ${rowData.phoneNumber || ""}`,
        designation: rowData.designation || "",
        hasBillingPortalAccess: rowData.hasBillingPortalAccess || false,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [rowData, mode]);

  const handleChange = (name) => (value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (duplicateContactError && (name === "mail" || name === "phoneNumber")) {
      setDuplicateContactError(null);
    }
  };

  const handleAddExistingContact = async () => {
    if (!duplicateContactError) return;
    setIsAddingExistingContact(true);
    try {
      const contactsResponse = await getContactByEmailApiCall({
        email: formData.mail,
        companyId,
      }).unwrap();
      const existingContact = contactsResponse?.contact;
      if (existingContact) {
        onSuccess?.(existingContact);
        onClose();
      } else {
        showToast("Contact not found. Please try again.", "error");
      }
    } catch (error) {
      showToast(error?.data?.message || "Error adding existing contact", "error");
    } finally {
      setIsAddingExistingContact(false);
      setDuplicateContactError(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.contactName.trim()) {
      showToast("Please enter a contact name", "error");
      return;
    }
    if (!formData.mail.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }
    if (!formData.designation.trim()) {
      showToast("Please enter a designation", "error");
      return;
    }

    setIsSubmitting(true);
    showToast(`${mode === "create" ? "Creating" : "Updating"} contact...`, "processing");

    const [countryCode, ...phoneRest] = formData.phoneNumber.split(" ");
    const payload = { ...formData, countryCode, phoneNumber: phoneRest.join(" "), companyId };

    try {
      const result =
        mode === "create"
          ? await createContactApiCall(payload).unwrap()
          : await updateContactApiCall({ id: rowData._id, data: payload }).unwrap();

      hideToast();
      showToast(`Contact ${mode === "create" ? "created" : "updated"} successfully`, "success");
      // Resolves with the plain contact record (not the {message, ...}
      // wrapper the API returns) - matches CompanyFormModal's onSuccess
      // contract, so openModal("create_contact")/("update_contact") can
      // hand the record straight back to whatever picker asked for it.
      onSuccess?.(mode === "create" ? result?.newContact : result?.updatedContact);
      onClose();
    } catch (error) {
      hideToast();
      const errorMessage =
        error?.data?.message ||
        `Error ${mode === "create" ? "creating" : "updating"} contact. Please try again.`;
      if (errorMessage.includes("Contact with this mail or phone number already exists")) {
        setDuplicateContactError({ message: errorMessage });
      } else {
        showToast(errorMessage, "error");
      }
    } finally {
      setIsSubmitting(false);
    }

    try {
      const dataResult = await getDataApiCall().unwrap();
      dispatch(setData(dataResult));
    } catch (error) {
      // Background refresh of shared app data - a failure here shouldn't
      // surface as if the contact save itself failed.
    }

    refetchFilters?.();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onRequestClose={onClose}
      title={mode === "create" ? "Add contact" : "Update contact"}
      width="600px"
      center
      isSubmitting={isSubmitting}
      footerButtons={[
        { children: "Cancel", variant: "outlined", onClick: onClose, disabled: isSubmitting },
        {
          children: mode === "create" ? "Create" : "Update",
          variant: "contained",
          onClick: handleSubmit,
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      <div className="contact-only-modal">
        <p className="contact-only-modal__description">
          {mode === "create"
            ? "Enter the contact details to create a new contact."
            : "Update the contact information below."}
        </p>

        {duplicateContactError && (
          <Banner
            type="warning"
            message={duplicateContactError.message}
            icon={UserPlus}
            buttonConfig={{
              variant: "text",
              text: isAddingExistingContact ? "Adding..." : "Add existing contact",
              onClick: handleAddExistingContact,
              disabled: isAddingExistingContact,
            }}
            onClose={() => setDuplicateContactError(null)}
          />
        )}

        <div className="contact-only-modal__grid">
          <TextInput
            label="Name"
            value={formData.contactName}
            onChange={handleChange("contactName")}
            disabled={isSubmitting}
          />
          <PhoneInput
            label="Phone number"
            value={formData.phoneNumber}
            onChange={handleChange("phoneNumber")}
            disabled={isSubmitting}
          />
          <TextInput
            label="Email"
            type="email"
            value={formData.mail}
            onChange={handleChange("mail")}
            disabled={isSubmitting}
          />
          <TextInput
            label="Designation"
            value={formData.designation}
            onChange={handleChange("designation")}
            disabled={isSubmitting}
          />
        </div>

        {companyId && (
          <Checkbox
            id="hasBillingPortalAccess"
            label="Billing portal access"
            description="Allow this contact to access the billing portal for invoice management and payment processing."
            value={formData.hasBillingPortalAccess}
            onChange={handleChange("hasBillingPortalAccess")}
            disabled={isSubmitting}
          />
        )}
      </div>
    </ModalShell>
  );
};

export default ContactOnlyModal;
